import React, { useState, type ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import AIIcon from './icons/AIIcon'
import CloudIcon from './icons/CloudIcon'
import ConsultingIcon from './icons/ConsultingIcon'
import Web3Icon from './icons/Web3Icon'
import AndroidIcon from './icons/AndroidIcon'
import AppStoreIcon from './icons/AppStoreIcon'
import FirewallIcon from './icons/FirewallIcon'
import AnalyticsIcon from './icons/AnalyticsIcon'
import ReactLogo from './icons/ReactLogo'
import NextjsLogo from './icons/NextjsLogo'
import AlternatingIcons from './icons/AlternatingIcons'

/**
 * The technology section on the About page (DMX-044).
 *
 * Every icon here means the technology it sits beside. That is worth stating because
 * the previous set did not: one card carried the real React mark while the other seven
 * carried generic outline shapes at 20px, so a brand logo sat beside a plain cloud
 * outline as though they were the same kind of thing.
 *
 * Four of these icons are the same components the Home page uses for the same ideas,
 * so the two pages now agree rather than illustrating one idea two ways.
 *
 * Two cards name two things each and show both in turn through AlternatingIcons. The
 * two brand marks are static SVG rather than Lottie, because animating somebody else's
 * trademark is not ours to do.
 *
 * The icons sit directly on the card with no tinted chip behind them, so the dark
 * surface is the icon's actual background. That rules out the site's default dark
 * treatment, which flattens an icon to a solid white silhouette and destroys anything
 * with filled interiors: the firewall, the analytics panel and the App Store mark all
 * came out as featureless white blobs.
 *
 * So these pass darkTreatment="none", which applies no filter at all: the artwork is
 * identical in both themes. The black outlines recede on a dark card while the blue
 * accents and white fills carry the shape, which works because every icon in this set
 * has enough filled area to read on its own. See darkTreatment.ts for the option that
 * was built to correct the strokes instead, and why it was rejected.
 */

/** 48px, the icons' native size. Sits directly on the card; there is no chip. */
const ICON_CLASS = 'w-12 h-12'

interface Technology {
  id: string
  name: string
  category: string
  icon: ReactNode
  description: string
}

const technologies: Technology[] = [
  {
    id: 'react',
    name: 'React & Next.js',
    category: 'Frontend',
    icon: (
      <AlternatingIcons
        className={ICON_CLASS}
        first={<ReactLogo className={ICON_CLASS} />}
        second={<NextjsLogo className={ICON_CLASS} />}
      />
    ),
    description: 'Modern web applications with cutting-edge frameworks and optimal performance'
  },
  {
    id: 'cloud',
    name: 'Cloud Solutions',
    category: 'Infrastructure',
    icon: <CloudIcon className={ICON_CLASS} darkTreatment="none" />,
    description: 'Scalable cloud architecture and deployment strategies for enterprise growth'
  },
  {
    id: 'mobile',
    name: 'Mobile Development',
    category: 'Mobile',
    icon: (
      <AlternatingIcons
        className={ICON_CLASS}
        first={<AppStoreIcon className={ICON_CLASS} darkTreatment="none" />}
        second={<AndroidIcon className={ICON_CLASS} darkTreatment="none" />}
      />
    ),
    description: 'Cross-platform mobile applications with native performance and user experience'
  },
  {
    id: 'ai',
    name: 'AI & Machine Learning',
    category: 'AI',
    icon: <AIIcon className={ICON_CLASS} darkTreatment="none" />,
    description: 'Intelligent automation and data insights that drive business transformation'
  },
  {
    id: 'security',
    name: 'Cybersecurity',
    category: 'Security',
    icon: <FirewallIcon className={ICON_CLASS} darkTreatment="none" />,
    description: 'Enterprise-grade security solutions protecting your digital assets and data'
  },
  {
    id: 'blockchain',
    name: 'Blockchain & Web3',
    category: 'Blockchain',
    icon: <Web3Icon className={ICON_CLASS} darkTreatment="none" />,
    description: 'Decentralized solutions and smart contract development for the future of business'
  },
  {
    id: 'consulting',
    name: 'Digital Consulting',
    category: 'Consulting',
    icon: <ConsultingIcon className={ICON_CLASS} darkTreatment="none" />,
    description: 'Strategic technology guidance and digital transformation roadmaps for leaders'
  },
  {
    id: 'analytics',
    name: 'Data Analytics',
    category: 'Analytics',
    icon: <AnalyticsIcon className={ICON_CLASS} darkTreatment="none" />,
    description: 'Advanced data processing and business intelligence solutions that inform decisions'
  }
]

const categories = ['All', 'Frontend', 'Infrastructure', 'Mobile', 'AI', 'Security', 'Blockchain', 'Consulting', 'Analytics']

const TechShowcase: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [hoveredTech, setHoveredTech] = useState<string | null>(null)

  const filteredTechnologies = selectedCategory === 'All' 
    ? technologies 
    : technologies.filter(tech => tech.category === selectedCategory)

  return (
    <section className="bg-white dark:bg-gray-900 py-24 transition-colors duration-500">
      <div className="container-custom">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-200">
            Our Technologies
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-lg transition-colors duration-200">
            We leverage cutting-edge technologies to deliver innovative solutions that transform businesses and empower growth.
          </p>
        </motion.div>

        <motion.div
          className="flex flex-wrap justify-center gap-2 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`
                px-4 py-2 text-sm rounded-full transition-all duration-300 relative overflow-hidden
                ${selectedCategory === category 
                  ? 'bg-primary-600 text-white shadow-lg shadow-primary-600/25' 
                  : 'text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700'
                }
              `}
            >
              {selectedCategory === category && (
                <motion.div
                  className="absolute inset-0 bg-primary-600"
                  layoutId="activeCategory"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                />
              )}
              <span className="relative z-10">{category}</span>
            </button>
          ))}
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          layout
        >
          <AnimatePresence mode="wait">
            {filteredTechnologies.map((tech, index) => (
              <motion.div
                key={tech.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ 
                  duration: 0.4, 
                  delay: index * 0.1
                }}
                onHoverStart={() => setHoveredTech(tech.id)}
                onHoverEnd={() => setHoveredTech(null)}
                className="group"
              >
                <div className="relative h-full bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 transition-all duration-300 hover:shadow-xl hover:shadow-primary-600/10 overflow-hidden">
                  <div className="p-6">
                    {/*
                      No chip. A 48px icon straight on the card, up from a 20px glyph
                      inside a tinted box. The icons are 48x48 natively, so this is
                      their intended size and nothing is being upscaled.
                    */}
                    <motion.div
                      className="mb-4"
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: "spring", bounce: 0.4 }}
                    >
                      {tech.icon}
                    </motion.div>

                    <div className="mb-3">
                      <span className="inline-block px-2 py-1 text-xs font-medium rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600">
                        {tech.category}
                      </span>
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 transition-colors duration-200">
                      {tech.name}
                    </h3>

                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed transition-colors duration-200">
                      {tech.description}
                    </p>

                    <motion.div
                      className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-primary-600 to-accent-500 rounded-b-xl"
                      initial={{ width: 0 }}
                      animate={{ width: hoveredTech === tech.id ? '100%' : 0 }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  )
}

export default TechShowcase